import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { store } from '../../store';
import { logout, updateTokens } from '../../store/slices/auth.slice';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000, // 60 seconds for large recording payloads
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - add auth token
    this.api.interceptors.request.use(
      (config) => {
        const state = store.getState();
        const token = state.auth.token;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 and we have a refresh token, try to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Don't redirect to login if we're already on login/register/auth pages or getting white-label config
          const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                                 originalRequest.url?.includes('/auth/register') ||
                                 originalRequest.url?.includes('/auth/forgot-password') ||
                                 originalRequest.url?.includes('/auth/reset-password') ||
                                 originalRequest.url?.includes('/white-label');

          if (isAuthEndpoint) {
            // Let the error propagate without redirect for auth endpoints and white-label
            return Promise.reject(error);
          }

          const state = store.getState();
          const refreshToken = state.auth.refreshToken;

          if (refreshToken) {
            try {
              const response = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {
                refreshToken,
              });

              const { token, refreshToken: newRefreshToken } = response.data.data;

              // Update tokens in store
              store.dispatch(updateTokens({ token, refreshToken: newRefreshToken }));

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.api(originalRequest);
            } catch (refreshError) {
              // Refresh failed, logout user
              store.dispatch(logout());
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          } else {
            // No refresh token, logout user
            store.dispatch(logout());
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url, config);
    return response.data;
  }
}

export default new ApiService();
